/**
 * SkiSupply — damped scrolling.
 *
 * A mouse wheel delivers a burst of large discrete jumps, which is what makes
 * a page feel like it is stepping rather than moving. This puts a spring
 * between the wheel and the scroll position: the wheel moves a target, and
 * the page eases toward it.
 *
 * WHICH ELEMENT SCROLLS IS NOT FIXED. Horizon sometimes scrolls the document
 * and sometimes an inner `.page-wrapper` — it changes with viewport and with
 * drawer state, and a script that resolves it once at load will silently stop
 * working the moment it flips. So it is resolved per event, every time.
 *
 * The whole risk of this technique is latency. Ease too hard and the page
 * feels like it is underwater and the user has lost control of it — the
 * common failure of every "smooth scroll" library. EASE below is deliberately
 * high for the genre: each frame closes a fifth of the remaining distance,
 * so 90% of a gesture lands in about ten frames — a sixth of a second. The
 * staircase goes; the sense that the page is attached to your hand does not.
 * Raise it toward 1 to weaken the effect; 1 is off entirely.
 *
 * What is deliberately left native, and why:
 *
 *   Touch and pens  — the OS already renders momentum, and smoothing it twice
 *     produces drift after the fingers have stopped.
 *   Keyboard, spacebar, Find-in-page, anchors  — these are how people who
 *     cannot use a wheel navigate. They must land where aimed, immediately.
 *   Anything with its own scrollbar  — the carousel, the cart drawer, a
 *     select popup. The wheel belongs to whatever is under the pointer.
 *
 * Nothing here is required for the page to scroll. If this script fails to
 * load, the wheel does exactly what it did before.
 */

(() => {
  'use strict';

  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  // A wheel is a mouse. On touch the OS momentum is already better than this.
  if (!matchMedia('(any-pointer: fine)').matches) return;

  const EASE = 0.2;      // fraction of remaining distance covered per frame
  const REST = 0.5;       // px below which we snap and stop

  let scroller = null;    // the element we are currently driving
  let target = 0;
  let frame = null;
  let previousBehavior = '';

  const canScroll = (el) =>
    !!el && el.scrollHeight > el.clientHeight + 1;

  /** Whichever element is actually the page scroller at this moment. */
  function activeScroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (canScroll(wrapper) && /auto|scroll|overlay/.test(getComputedStyle(wrapper).overflowY)) {
      return wrapper;
    }
    const doc = document.scrollingElement || document.documentElement;
    return canScroll(doc) ? doc : null;
  }

  const limit = (el) => Math.max(0, el.scrollHeight - el.clientHeight);

  /* Does something between the pointer and the page want this wheel? A nested
     scroller only counts if it can still move in the direction asked for —
     otherwise a carousel scrolled to its end would swallow the page scroll. */
  function nestedScrollerWants(node, direction, page) {
    while (node && node !== page && node.nodeType === 1) {
      if (node.matches?.('dialog[open], [aria-modal="true"]')) return true;

      const style = getComputedStyle(node);
      const scrollable =
        /auto|scroll|overlay/.test(style.overflowY) &&
        node.scrollHeight > node.clientHeight + 1;

      if (scrollable) {
        const atTop = node.scrollTop <= 0;
        const atEnd = node.scrollTop + node.clientHeight >= node.scrollHeight - 1;
        if (!(direction < 0 && atTop) && !(direction > 0 && atEnd)) return true;
      }
      node = node.parentElement;
    }
    return false;
  }

  function release() {
    if (scroller) scroller.style.scrollBehavior = previousBehavior;
    frame = null;
  }

  function step() {
    if (!scroller) return release();

    const current = scroller.scrollTop;
    const distance = target - current;

    if (Math.abs(distance) < REST) {
      scroller.scrollTop = target;
      return release();
    }

    scroller.scrollTop = current + distance * EASE;
    frame = requestAnimationFrame(step);
  }

  function run() {
    if (frame !== null) return;
    // A CSS `scroll-behavior: smooth` would animate every frame we write,
    // stacking its curve on top of ours. Suspend it while we drive.
    previousBehavior = scroller.style.scrollBehavior;
    scroller.style.scrollBehavior = 'auto';
    frame = requestAnimationFrame(step);
  }

  window.addEventListener(
    'wheel',
    (event) => {
      if (event.ctrlKey) return;                                     // pinch zoom
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;   // sideways

      const page = activeScroller();
      if (!page) return;
      if (nestedScrollerWants(event.target, event.deltaY, page)) return;

      // The scroller flipped since the last gesture: start from where it is.
      if (page !== scroller) {
        if (frame !== null) {
          cancelAnimationFrame(frame);
          release();
        }
        scroller = page;
        target = page.scrollTop;
      }

      // deltaMode 1 is lines, 2 is pages. Normalise both to pixels.
      let delta = event.deltaY;
      if (event.deltaMode === 1) delta *= 16;
      else if (event.deltaMode === 2) delta *= page.clientHeight;

      event.preventDefault();
      target = Math.max(0, Math.min(limit(page), target + delta));
      run();
    },
    { passive: false }
  );

  /* Anything that scrolls the page by other means — a keypress, an anchor, a
     scrollbar drag, scrollIntoView — moves the element without telling us.
     While we are not driving, the target follows reality, so the next wheel
     event starts from where the page actually is rather than snapping back. */
  document.addEventListener(
    'scroll',
    () => {
      if (frame === null && scroller) target = scroller.scrollTop;
    },
    { capture: true, passive: true }
  );

  // A backgrounded tab stops painting; resuming mid-glide would jump.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) return;
    if (frame !== null) {
      cancelAnimationFrame(frame);
      release();
    }
    if (scroller) target = scroller.scrollTop;
  });

  document.documentElement.classList.add('ss-smooth-scroll');
})();
